import { Globe, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { Seo } from "@/components/brand/Seo";
import {
  BorderBox,
  DarkSection,
  DotList,
  Eyebrow,
  GhostButton,
  OrangeButton,
  Section,
} from "@/components/brand/Bits";
import { CONTACT, SCOPING } from "@/data/siteContent";

const CHANNELS = [
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testId: "contact-email" },
  { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, testId: "contact-phone" },
  {
    icon: Instagram,
    label: "Instagram",
    value: CONTACT.instagram,
    href: CONTACT.instagramUrl,
    external: true,
    testId: "contact-instagram",
  },
  {
    icon: Globe,
    label: "Website",
    value: CONTACT.website,
    href: CONTACT.websiteUrl,
    external: true,
    testId: "contact-website",
  },
];

export default function Contact() {
  return (
    <>
      <Seo
        path="/contact"
        title="Contact AnyTribe — Scope Your Next Creator Campaign"
        description="Send us the objective, the window and a budget range. We'll come back with a creator mix, indicative reach and a cost per outcome — not a rate card. Email hello@anytribemarketing.com."
      />

      <DarkSection testId="contact-hero" className="pt-20 sm:pt-24">
        <div className="max-w-3xl">
          <Eyebrow dark>{SCOPING.eyebrow}</Eyebrow>
          <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl">
            {SCOPING.title}
          </h1>
          <p className="mt-6 text-base leading-relaxed text-white/75 md:text-lg">{SCOPING.body}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <OrangeButton
              href={`mailto:${CONTACT.email}?subject=Campaign%20brief%20-%20AnyTribe`}
              testId="contact-hero-email"
            >
              Send us a brief
            </OrangeButton>
            <GhostButton href={CONTACT.phoneHref} dark testId="contact-hero-call">
              Call {CONTACT.phone}
            </GhostButton>
          </div>
        </div>
      </DarkSection>

      <Section testId="contact-steps">
        <div className="grid gap-4 md:grid-cols-3">
          {SCOPING.steps.map((step) => (
            <BorderBox key={step.n} testId={`contact-${step.n.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#F2811D]">
                {step.n}
              </p>
              <h2 className="mt-3 font-heading text-lg font-extrabold">{step.title}</h2>
              <p className="mt-2.5 text-[15px] leading-relaxed body-text">{step.body}</p>
            </BorderBox>
          ))}
        </div>
      </Section>

      <Section tone="cream" testId="contact-details">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-16">
          <div>
            <Eyebrow>Get in touch</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              {CONTACT.headline}
            </h2>
            <p className="mt-5 text-base leading-relaxed body-text md:text-lg">{CONTACT.body}</p>

            <dl className="mt-9 space-y-5">
              {CHANNELS.map((channel) => (
                <div key={channel.label} className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white">
                    <channel.icon className="h-4.5 w-4.5 text-[#D96C0C]" />
                  </span>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8B857C]">
                      {channel.label}
                    </dt>
                    <dd className="mt-0.5">
                      <a
                        href={channel.href}
                        {...(channel.external
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                        className="font-heading text-base font-extrabold hover:text-[#F2811D]"
                        data-testid={channel.testId}
                      >
                        {channel.value}
                      </a>
                    </dd>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-4">
                <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white">
                  <MapPin className="h-4.5 w-4.5 text-[#D96C0C]" />
                </span>
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8B857C]">
                    Based in
                  </dt>
                  <dd className="mt-0.5 font-heading text-base font-extrabold">{CONTACT.location}</dd>
                </div>
              </div>
            </dl>
          </div>

          <div className="space-y-4 self-start">
            <BorderBox testId="contact-brands-card">
              <h3 className="font-heading text-lg font-extrabold">For brands and agencies</h3>
              <p className="mt-2.5 text-[15px] leading-relaxed body-text">
                Objective, audience, timing and budget range. A paragraph is enough to start — we'll
                return a creator mix by tier, projected reach, deliverables and cost per outcome.
              </p>
              <div className="mt-5">
                <OrangeButton
                  href={`mailto:${CONTACT.email}?subject=Campaign%20brief%20-%20AnyTribe`}
                  testId="contact-brand-cta"
                >
                  Send your brief
                </OrangeButton>
              </div>
            </BorderBox>

            <BorderBox testId="contact-creators-card">
              <h3 className="font-heading text-lg font-extrabold">For creators and KOLs</h3>
              <p className="mt-2.5 text-[15px] leading-relaxed body-text">
                We work with nano to premium creators across beauty, skincare, wellness, personal
                care, lifestyle, food and parenting. Apply with your handles, categories and rates —
                it takes about two minutes.
              </p>
              <div className="mt-5">
                <OrangeButton to="/join" testId="contact-creator-cta">
                  Join the creator pool
                </OrangeButton>
              </div>
            </BorderBox>

            <BorderBox testId="contact-salt-soul-card">
              <h3 className="font-heading text-lg font-extrabold">
                {CONTACT.saltAndSoul.name} · Events & community
              </h3>
              <p className="mt-2.5 text-[15px] leading-relaxed body-text">
                {CONTACT.saltAndSoul.description}
              </p>
              <a
                href={CONTACT.saltAndSoul.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 font-bold text-[#F2811D] hover:underline"
                data-testid="contact-salt-soul-link"
              >
                <Instagram className="h-4 w-4" />
                {CONTACT.saltAndSoul.handle}
              </a>
            </BorderBox>
          </div>
        </div>
      </Section>

      <DarkSection testId="contact-what-to-send" tight>
        <div className="max-w-3xl">
          <Eyebrow dark>What to send</Eyebrow>
          <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
            Four lines is enough to get started
          </h2>
          <DotList
            className="mt-8"
            dark
            items={[
              "The objective — awareness, trial, registrations, retail visibility or search demand.",
              "The audience you need to influence, not the creators you have in mind.",
              "The window — launch date, promo cycle or event date.",
              "A budget range, so we can shape the creator mix by tier around it.",
            ]}
            testId="what-to-send-list"
          />
        </div>
      </DarkSection>
    </>
  );
}
